import { useState, useEffect, useRef, useMemo } from 'react';
import { format, parse } from 'date-fns';
import { Upload, Check } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import {
  getCourtRate, parseTimeSlotStartHour, formatTimeSlotRange, getCourtLabel,
  isSharedOwnershipEnabled, getCourtOwner, getOwnerQrCodes, getOwnerLabel,
} from '../utils/bookingUtils';
import { isPackageBooking } from '../utils/packageUtils';
import PackageBookingBox from './PackageBookingBox';
import BookingTotalRow from './BookingTotalRow';
import PaymentQrDisplay from './PaymentQrDisplay';
import { getPartialPaymentPercent, isPartialPaymentEnabled } from '../utils/partialPaymentUtil';
import { uploadPaymentScreenshot } from '../utils/imageUploadUtil';
import '../css/CheckoutSummary.css';
// Reuses UserInfoForm's shared modal-card layout (uif-* classes / .user-info-form).
import '../css/UserInfoForm.css';
import { formatMoney } from '../utils/currencyUtil';
import { formatCountdown } from '../utils/countdownUtil';
// Step headings that split the payment part of the card into the two things
// the guest has to do, in order. Rendered as a div with a heading role, not
// an <h3>: the mobile rules in CheckoutSummary.css shrink every h3 in this
// card to 0.85rem.
import { PAYMENT_STEP_LABELS } from '../constants/bookingConst';
import { ENTITY_TYPES, GUEST_ACTOR } from '../utils/auditLogUtil';

// 4. Checkout & Payment Summary Component — matches jassen's checkout screen
// (checkoutpage.png / checkoutpage2.png): booking details recap, a live
// payment countdown, a QR-code picker, a downloadable reference PDF, and the
// payment-receipt upload that used to live on the confirmation screen.
function CheckoutSummary({ bookingData, bookingKeys = null, onPaymentDone, onReceiptUploaded, onExpire, onBack }) {
  const { config } = useConfig();
  const fileInputRef = useRef(null);

  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // Starts true for a booking that was already submitted — the checkout tab
  // can be reloaded after the receipt went in, and must not ask for it again.
  const [uploadSuccess, setUploadSuccess] = useState(() => bookingData?.status === 'pending');
  // The guest's "Partial payment" choice, carried over from the Booking
  // details card and still changeable here until the receipt is attached —
  // that upload saves it onto the booking (see CheckoutPage).
  const [partialPayment, setPartialPayment] = useState(() => Boolean(bookingData?.guestPartialPayment));
  const [msLeft, setMsLeft] = useState(() =>
    bookingData?.paymentDeadline ? new Date(bookingData.paymentDeadline).getTime() - Date.now() : null
  );

  // The owner this booking's courts belong to, when Shared Ownership is on
  // (Config -> System). App.jsx refuses to let a guest mix owners in one
  // selection, so every court here resolves to the same owner and the first
  // one found is authoritative. Null whenever the setting is off.
  const bookingOwner = useMemo(() => {
    if (!isSharedOwnershipEnabled(config)) return null;
    const dateEntries = Array.isArray(bookingData?.dates) && bookingData.dates.length > 0
      ? bookingData.dates
      : [{ timeSlots: bookingData?.timeSlots }];
    const bookedCourts = dateEntries.flatMap(entry => Object.values(entry?.timeSlots || {}).flat());
    return bookedCourts.map(court => getCourtOwner(config, court)).find(Boolean) || null;
  }, [config, bookingData]);

  // Payment methods: prefer the admin's multi-QR list, fall back to the
  // single legacy QR fields if that's all that's configured.
  //
  // Under shared ownership this narrows to JUST the owning account's QR
  // codes — the guest is paying that owner directly, so the club-wide list
  // would be showing them accounts the money must not go to. Deliberately
  // NOT falling back to the club list when the owner has none: sending a
  // guest to the wrong account is worse than telling them to call. (That
  // state should be unreachable anyway — such a court can't be selected —
  // but a QR deleted mid-booking would land here.)
  const paymentMethods = useMemo(() => {
    if (isSharedOwnershipEnabled(config)) {
      return getOwnerQrCodes(bookingOwner).map(qr => ({
        id: qr.id,
        name: qr.name || getOwnerLabel(bookingOwner),
        url: qr.url,
      }));
    }
    if (config?.qrCodes && config.qrCodes.length > 0) {
      return config.qrCodes.map(qr => ({ id: qr.id, name: qr.name || 'Payment QR', url: qr.url }));
    }
    if (config?.paymentQrUrl) {
      return [{ id: 'legacy-qr', name: config?.bankName || 'Payment', url: config.paymentQrUrl }];
    }
    return [];
  }, [config, bookingOwner]);

  const [selectedQrId, setSelectedQrId] = useState(paymentMethods[0]?.id || '');
  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethods.some(m => m.id === selectedQrId)) {
      setSelectedQrId(paymentMethods[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods]);
  const selectedMethod = paymentMethods.find(m => m.id === selectedQrId) || paymentMethods[0];

  // What a "Download QR" is recorded against on the audit trail: the
  // booking's own id, the same one its booking_submitted entry uses (the
  // group id for a multi-date booking, else the first record's key), so the
  // download shows up in that booking's history.
  const qrAudit = useMemo(() => {
    const entityId = bookingData?.groupId || bookingKeys?.[0] || bookingData?.confirmationId;
    if (!entityId) return null;
    return {
      entityType: ENTITY_TYPES.BOOKING,
      entityId,
      performedBy: bookingData?.userName || GUEST_ACTOR,
      summary: { confirmationId: bookingData?.confirmationId || null, date: bookingData?.date || null },
    };
  }, [bookingData, bookingKeys]);

  // Live countdown — ticks every second while no receipt has been attached
  // yet. Once the deadline passes without a receipt, release the slot.
  useEffect(() => {
    if (!bookingData?.paymentDeadline || uploadSuccess) return undefined;
    const tick = () => {
      const remaining = new Date(bookingData.paymentDeadline).getTime() - Date.now();
      setMsLeft(remaining);
      if (remaining <= 0) {
        onExpire?.();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [bookingData?.paymentDeadline, uploadSuccess, onExpire]);

  // Multi-date bookings (see App.jsx's createPendingBooking) carry a
  // `dates` array instead of just one date/timeSlots pair — fall back to
  // treating the single fields as a one-entry list for an ordinary
  // single-date booking. Mirrors ConfirmationScreen.jsx's identical
  // dateGroups pattern, so this recap looks the same as what the guest
  // sees again once payment is confirmed.
  const dateGroups = Array.isArray(bookingData?.dates) && bookingData.dates.length > 0
    ? bookingData.dates
    : [{ date: bookingData?.date, timeSlots: bookingData?.timeSlots }];

  // The processing fee as ACTUALLY CHARGED — read off the booking record, not
  // recomputed from config, so the line can never disagree with the total
  // below it if an admin edits the fee mid-session.
  const bookingFeeAmount = Number(bookingData?.bookingFee) || 0;

  // An event package: one block, one price, never itemised per hour — see
  // PackageBookingBox.
  const showPackageBox = isPackageBooking(bookingData);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setScreenshot(file);
    setUploadError(null);
    setUploading(true);
    try {
      // Attaching the receipt is what actually submits the booking — it
      // flips status to 'pending' (awaiting admin review) and sends the
      // guest their confirmation email. "Payment Done" below just moves the
      // guest forward to the confirmation screen.
      await uploadPaymentScreenshot(file, bookingData.confirmationId);
      setUploadSuccess(true);
      onReceiptUploaded?.(partialPayment);
    } catch (error) {
      setUploadError(error.message);
      setScreenshot(null);
    } finally {
      setUploading(false);
    }
  };

  if (!bookingData) return null;

  return (
    <div className="user-info-form checkout-summary">
      <div className="uif-card cs-card">
        <div className="uif-head">
          <h2 className="uif-title">Booking details</h2>
          {/* Stops being a cancel button the moment the receipt is attached —
              at that point the booking is submitted, and onBack takes the
              guest to their confirmation instead of discarding it (see
              handleBack in CheckoutPage.jsx). The label has to change with it,
              or a screen reader announces "Cancel booking" for a control that
              no longer cancels. */}
          {onBack && (
            <button
              type="button"
              className="uif-close"
              onClick={onBack}
              aria-label={uploadSuccess ? 'Close' : 'Cancel booking'}
              title={uploadSuccess ? 'Close' : 'Cancel booking'}
            >&times;</button>
          )}
        </div>

        {showPackageBox ? (
          /* Sold as one block — the per-hour court rows below would show
             hourly rates that don't add up to what was charged. */
          <PackageBookingBox bookingData={bookingData} />
        ) : dateGroups.map((group, idx) => {
          const courtGroups = {};
          Object.entries(group.timeSlots || {}).forEach(([timeSlot, courts]) => {
            (courts || []).forEach(court => {
              if (!courtGroups[court]) courtGroups[court] = [];
              // group.date scopes the lookup to the right weekday — rate bands
              // can be set per day (see getCourtRate in bookingUtils.js).
              courtGroups[court].push({ timeSlot, price: getCourtRate(config, court, timeSlot, null, group.date) });
            });
          });
          const sortedCourts = Object.keys(courtGroups).map(Number).sort((a, b) => a - b);
          sortedCourts.forEach(court => {
            courtGroups[court].sort((a, b) => parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot));
          });
          const groupDateLabel = group.date
            ? format(parse(group.date, 'yyyy-MM-dd', new Date()), 'EEEE, MMMM d, yyyy')
            : '';

          return (
            <div className="uif-booking-box" key={group.date || idx}>
              <h3>{groupDateLabel}</h3>
              {sortedCourts.map(court => (
                <div key={court}>
                  <div className="uif-court-lbl">{getCourtLabel(config, court)}</div>
                  {courtGroups[court].map(row => (
                    <div className="uif-row" key={row.timeSlot}>
                      <span className="uif-time">{formatTimeSlotRange(row.timeSlot)}</span>
                      <span className="uif-fee">{formatMoney(row.price, config)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })}

        {bookingData.equipmentRental?.items?.length > 0 && (
          <div className="uif-booking-box">
            <h3>Equipment rental</h3>
            {bookingData.equipmentRental.items.map(item => (
              <div className="uif-row" key={item.id}>
                <span className="uif-time">{item.label} &times; {item.qty}</span>
                <span className="uif-fee">{formatMoney(item.subtotal, config)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Itemised so the Total below adds up on screen — the fee used to be
            folded into it silently. Shown as one plain line: whether it was
            charged flat or per hour is the club's business, not something a
            guest needs the arithmetic for. */}
        {bookingFeeAmount > 0 && (
          <div className="uif-fee-row">
            <span className="uif-time">Processing fee</span>
            <span className="uif-fee">{formatMoney(bookingFeeAmount, config)}</span>
          </div>
        )}

        <BookingTotalRow
          idPrefix="cs"
          total={Number(bookingData.paymentAmount || 0)}
          config={config}
          showPartialOption={isPartialPaymentEnabled(config) && Number(bookingData.paymentAmount || 0) > 0}
          partialChecked={partialPayment}
          onPartialChange={setPartialPayment}
          partialPercent={getPartialPaymentPercent(config)}
          partialLocked={uploadSuccess}
        />

        <div className="cs-step-label" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.PAY}</div>

        {msLeft !== null && !uploadSuccess && (
          <div className="cs-timer">
            Complete payment within <strong className="cs-timer-count">{formatCountdown(msLeft)}</strong> or your reservation will be released.
          </div>
        )}

        {paymentMethods.length > 0 ? (
          <>
            {/* Under shared ownership the guest is paying one court owner
                directly, not the club — naming them here is what makes an
                unfamiliar account name on the QR make sense. */}
            <div className="cs-qr-label">
              {bookingOwner ? `Choose payment QR — ${getOwnerLabel(bookingOwner)}` : 'Choose payment QR'}
            </div>
            <select
              className="cs-qr-select"
              value={selectedQrId}
              onChange={(e) => setSelectedQrId(e.target.value)}
            >
              {paymentMethods.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>

            {/* Tap the QR for a full-screen view; "Download QR" under it.
                Keyed by the QR so a failed-download note doesn't carry over
                to the next QR picked. */}
            {selectedMethod && (
              <PaymentQrDisplay key={selectedMethod.id} method={selectedMethod} audit={qrAudit} />
            )}

            <p className="cs-note cs-note-amber">
              Before you send, please double-check you're paying to the account shown in the QR above — just to be sure your payment goes to the right account.
            </p>
          </>
        ) : (
          <p className="cs-note cs-note-amber">
            {isSharedOwnershipEnabled(config)
              ? <>The owner of this court hasn&apos;t set up a payment QR code yet, so we can&apos;t show you one here.
                  Please contact {config?.courtName || 'us'}{config?.contactNumber ? ` at ${config.contactNumber}` : ''} to
                  arrange payment — your reservation reference is on the confirmation screen.</>
              : <>No payment QR codes have been configured yet. Please contact {config?.courtName || 'us'} directly to arrange payment.</>}
          </p>
        )}

        <div className="cs-step-label" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.UPLOAD}</div>

        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className={`cs-upload-btn ${uploadSuccess ? 'cs-upload-btn-done' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploadSuccess ? (
            <><Check size={16} /> Receipt uploaded{screenshot ? ` — ${screenshot.name}` : ''}</>
          ) : uploading ? (
            'Uploading…'
          ) : (
            <><Upload size={16} /> Upload Payment Receipt</>
          )}
        </button>
        {uploadError && <p className="cs-upload-error">{uploadError}</p>}

        <button
          type="button"
          className="cs-payment-done-btn"
          disabled={!uploadSuccess}
          onClick={onPaymentDone}
        >
         Submit Booking
        </button>
      </div>
    </div>
  );
}

export default CheckoutSummary;
